import { Box, IconButton, Tooltip, Chip, Typography } from "@mui/joy";
import { Info as InfoIcon } from "lucide-react";
import { useMemo } from "react";

/**
 * Info cell renderer - opens edit form for inventory item
 */
export function Info(props: any) {
    const { value, data, onEdit } = props;

    const handleClick = () => {
        if (onEdit && data) {
            onEdit(data);
        }
    };

    return (
        <Box display={"flex"} justifyContent={"center"} alignItems={"center"} height={"100%"}>
            <Tooltip title="View details" placement="top">
                <IconButton
                    size="sm"
                    variant="soft"
                    color="primary"
                    onClick={handleClick}
                    sx={{ "--IconButton-size": "32px" }}
                >
                    <InfoIcon size={16} />
                </IconButton>
            </Tooltip>
        </Box>
    );
}

/**
 * Status chip component with color coding
 */
export function StatusChip({ status }: { status: string }) {
    const getStatusConfig = (status: string) => {
        const upperStatus = status?.toUpperCase() || "";
        switch (upperStatus) {
            case "ACTIVE":
                return { color: "success" as const, label: "Active" };
            case "INACTIVE":
                return { color: "neutral" as const, label: "Inactive" };
            default:
                return { color: "neutral" as const, label: status || "Unknown" };
        }
    };

    const config = getStatusConfig(status);
    return (
        <Chip
            variant="soft"
            color={config.color}
            size="md"
            sx={{ borderRadius: "4px", border: "1px solid", borderColor: "divider" }}
        >
            {config.label}
        </Chip>
    );
}

/**
 * Status cell renderer
 */
export function StatusCellRenderer(props: any) {
    const { value } = props;
    return <StatusChip status={value} />;
}

/**
 * Unit details cell renderer - combines Unit Type, Unit Size, and Unit Price
 */
export function UnitDetailsRenderer(props: any) {
    const { data } = props;

    const parts = useMemo(() => {
        if (!data) return [];

        const unitType = data.SupplierUnitType || "";
        const unitSize = data.SupplierUnitSize != null ? String(data.SupplierUnitSize) : "";
        // Always show price - format it properly
        const rawPrice = data.UnitPrice;
        const unitPrice = rawPrice != null && rawPrice !== undefined ? `$${Number(rawPrice).toFixed(2)}` : "$0.00";

        const result: string[] = [];
        // Format: "$85.00 / bundle (4 units)" or "$85.00 / bundle" if no size
        if (unitType) {
            if (unitSize) {
                result.push(`${unitPrice} / ${unitType} (${unitSize} units)`);
            } else {
                result.push(`${unitPrice} / ${unitType}`);
            }
        } else if (unitPrice) {
            result.push(unitPrice);
        }

        return result;
    }, [data?.SupplierUnitType, data?.SupplierUnitSize, data?.UnitPrice]);

    if (parts.length === 0) return <Box>-</Box>;

    return (
        <Box display="flex" flexDirection="column" gap={0.5} height="100%" justifyContent="center">
            {parts.map((part: string, index: number) => (
                <Typography key={index} level="body-sm">
                    {part}
                </Typography>
            ))}
        </Box>
    );
}
